export class CartManager {
  constructor() {
    this.items = [];
    this.listeners = [];
    this.pendingOrder = null;
    this.orderState = 'cart';
  }

  addItem(item) {
    const normalizedItem = this.prepareCartItem(item);

    const existingItemIndex = this.items.findIndex(cartItem =>
      cartItem.id === normalizedItem.id &&
      this.serializeModifiers(cartItem.modifiers) === this.serializeModifiers(normalizedItem.modifiers) &&
      this.normalizeSpecialInstructions(cartItem.specialInstructions) === this.normalizeSpecialInstructions(normalizedItem.specialInstructions)
    );

    if (existingItemIndex !== -1) {
      const existingItem = this.items[existingItemIndex];
      existingItem.quantity += normalizedItem.quantity;
      existingItem.unitPrice = normalizedItem.unitPrice;
      existingItem.price = normalizedItem.unitPrice;
      existingItem.totalPrice = existingItem.unitPrice * existingItem.quantity;
      existingItem.lastAddedAt = Date.now();
    } else {
      this.items.push({
        ...normalizedItem,
        cartId: Date.now() + Math.random(),
        lastAddedAt: Date.now()
      });
    }

    this.notifyListeners();
    return this.getItemCount();
  }

  removeItem(cartId) {
    this.items = this.items.filter(item => item.cartId !== cartId);
    this.notifyListeners();
    return this.getItemCount();
  }

  updateItemQuantity(cartId, quantity) {
    const item = this.items.find(item => item.cartId === cartId);
    if (item) {
      if (quantity <= 0) {
        this.removeItem(cartId);
      } else {
        item.quantity = quantity;
        const unitPrice = this.resolveUnitPrice(item);
        item.unitPrice = unitPrice;
        item.price = unitPrice;
        item.totalPrice = unitPrice * quantity;
        this.notifyListeners();
      }
    }
    return this.getItemCount();
  }

  getItems() {
    return this.items;
  }

  getItemCount() {
    return this.items.reduce((total, item) => total + item.quantity, 0);
  }

  getTotals() {
    if (this.orderState === 'pending-payment' && this.pendingOrder && this.pendingOrder.total != null) {
      const backendTotal = Number(this.pendingOrder.total);
      const safeTotal = Number.isNaN(backendTotal) ? 0 : backendTotal;
      return {
        subtotal: safeTotal,
        total: safeTotal
      };
    }

    const subtotal = this.items.reduce((total, item) => {
      const unitPrice = this.resolveUnitPrice(item);
      return total + (item.totalPrice || (unitPrice * item.quantity));
    }, 0);

    return {
      subtotal,
      total: subtotal
    };
  }

  clear() {
    this.items = [];
    this.pendingOrder = null;
    this.orderState = 'cart';
    this.notifyListeners();
  }

  setPendingOrder(order) {
    this.pendingOrder = order;
    this.orderState = 'pending-payment';
    this.notifyListeners();
  }

  completeOrder() {
    this.items = [];
    this.pendingOrder = null;
    this.orderState = 'completed';
    this.notifyListeners();

    setTimeout(() => {
      this.orderState = 'cart';
      this.notifyListeners();
    }, 100);
  }

  cancelPendingOrder() {
    this.pendingOrder = null;
    this.orderState = 'cart';
    this.notifyListeners();
  }

  getPendingOrder() {
    return this.pendingOrder;
  }

  getOrderState() {
    return this.orderState;
  }

  isPendingPayment() {
    return this.orderState === 'pending-payment';
  }

  addListener(callback) {
    this.listeners.push(callback);
  }

  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.items, this.getItemCount(), this.getTotals());
      } catch (error) {
        console.error('Error in cart listener:', error);
      }
    });
  }

  prepareCartItem(item = {}) {
    const quantity = Number(item.quantity) > 0 ? Number(item.quantity) : 1;
    const unitPrice = this.resolveUnitPrice(item);
    const normalizedModifiers = item.modifiers == null ? [] : item.modifiers;

    return {
      ...item,
      quantity,
      unitPrice,
      price: unitPrice,
      totalPrice: unitPrice * quantity,
      modifiers: normalizedModifiers,
      specialInstructions: this.normalizeSpecialInstructions(item.specialInstructions)
    };
  }

  resolveUnitPrice(item = {}) {
    if (typeof item.price === 'number') {
      return item.price;
    }

    if (typeof item.unitPrice === 'number') {
      return item.unitPrice;
    }

    if (item.price && typeof item.price.amount === 'number') {
      return item.price.amount;
    }

    if (item.unitPrice && typeof item.unitPrice.amount === 'number') {
      return item.unitPrice.amount;
    }

    return 0;
  }

  normalizeSpecialInstructions(value) {
    return (value || '').trim();
  }

  serializeModifiers(modifiers) {
    if (!modifiers) {
      return '[]';
    }

    if (Array.isArray(modifiers)) {
      const normalized = modifiers.map(mod => {
        if (mod && typeof mod === 'object') {
          const group = mod.groupId || mod.group || mod.category || mod.id || mod.name || '';
          const option = mod.optionId || mod.value || mod.id || mod.name || '';
          return { group, option };
        }
        return { value: mod };
      }).sort((a, b) => {
        const aKey = `${a.group || ''}:${a.option || a.value || ''}`;
        const bKey = `${b.group || ''}:${b.option || b.value || ''}`;
        return aKey.localeCompare(bKey);
      });
      return JSON.stringify(normalized);
    }

    if (typeof modifiers === 'object') {
      const entries = Object.entries(modifiers).map(([group, optionValue]) => {
        if (optionValue && typeof optionValue === 'object') {
          const optionId = optionValue.id || optionValue.value || optionValue.name || optionValue;
          return { group, option: optionId };
        }
        return { group, option: optionValue };
      }).sort((a, b) => {
        const aKey = `${a.group || ''}:${a.option || ''}`;
        const bKey = `${b.group || ''}:${b.option || ''}`;
        return aKey.localeCompare(bKey);
      });
      return JSON.stringify(entries);
    }

    return JSON.stringify(modifiers);
  }
}
